import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Calendar as CalendarIcon,
  Download,
  Clock,
  CheckCircle,
  TrendingUp,
  AlertTriangle,
  Award,
  Users,
  Search,
  SlidersHorizontal,
  ChevronDown
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area
} from "recharts";

const COLORS = ["#0EA5E9", "#10B981", "#F43F5E", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6"];

const DashboardBuilderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const dateFrom = format(dateRange.from, "yyyy-MM-dd");
      const dateTo = format(dateRange.to, "yyyy-MM-dd");
      const res = await authAxios.get(`/dashboards/${id}?date_from=${dateFrom}&date_to=${dateTo}`);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching dashboard builder:", error);
      toast.error("Error al cargar la configuración del dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [id, dateRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center p-8">
        <h2 className="text-xl font-bold text-slate-800">Dashboard no encontrado</h2>
        <p className="text-slate-500 mt-2">La plantilla del departamento no está disponible.</p>
      </div>
    );
  }

  const KPICard = ({ title, value, subtitle, icon: Icon, color = "sky" }) => {
    const colorClasses = {
      emerald: "text-emerald-600 bg-emerald-100 dark:bg-emerald-950/20",
      sky: "text-sky-600 bg-sky-100 dark:bg-sky-950/20",
      rose: "text-rose-600 bg-rose-100 dark:bg-rose-950/20",
      indigo: "text-indigo-600 bg-indigo-100 dark:bg-indigo-950/20",
      amber: "text-amber-600 bg-amber-100 dark:bg-amber-950/20",
      purple: "text-purple-600 bg-purple-100 dark:bg-purple-950/20"
    };

    return (
      <Card className="kpi-card card-hover transition-all duration-300 hover:shadow-lg border-l-4 border-l-sky-500">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-extrabold mt-2 text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color] || colorClasses.sky}`}>
            {Icon ? <Icon className="w-6 h-6" /> : <TrendingUp className="w-6 h-6" />}
          </div>
        </div>
      </Card>
    );
  };

  const filteredTable = data.table_data?.filter(row => 
    row.agent?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.summary?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-sky-100 text-sky-700 text-xs font-semibold rounded-full uppercase tracking-wider">
              Métricas de Flujo
            </span>
            <span className="text-xs text-slate-400">ID: {id}</span>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-950 mt-2">{data.title}</h1>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">{data.purpose}</p>
        </div>

        <div className="flex items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2 border-slate-200 hover:bg-slate-50" data-testid="date-filter-btn">
                <CalendarIcon className="w-4 h-4 text-slate-500" />
                {format(dateRange.from, "dd MMM", { locale: es })} - {format(dateRange.to, "dd MMM", { locale: es })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={(range) => range && setDateRange(range)}
                numberOfMonths={2}
                locale={es}
              />
            </PopoverContent>
          </Popover>

          <Button variant="outline" className="border-sky-200 text-sky-700 hover:bg-sky-50 shadow-sm flex items-center gap-2">
            <Download className="w-4 h-4" />
            <span>Exportar CSV</span>
          </Button>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {data.kpis?.map((kpi, idx) => (
          <KPICard 
            key={idx}
            title={kpi.title}
            value={kpi.value}
            subtitle={kpi.subtitle}
            color={kpi.color}
            icon={idx === 0 ? CheckCircle : idx === 1 ? Clock : idx === 2 ? AlertTriangle : Award}
          />
        ))}
      </div>

      {/* Dynamic Widget Grid based on ID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Widget 1: Clasificación de Gestión */}
        {data.charts?.classification && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="border-b border-slate-50 pb-4">
              <CardTitle className="text-lg font-bold text-slate-800">Clasificación de la Gestión</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.charts.classification}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }} />
                    <Bar dataKey="value" fill="#0EA5E9" radius={[4, 4, 0, 0]}>
                      {data.charts.classification.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Widget 2: FCR o Contacto Efectivo */}
        {(data.charts?.fcr || data.charts?.effective_contact) && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="border-b border-slate-50 pb-4">
              <CardTitle className="text-lg font-bold text-slate-800">
                {id === "200" ? "Contacto Efectivo" : "First Call Resolution (FCR)"}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.charts.effective_contact || data.charts.fcr}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={95}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {(data.charts.effective_contact || data.charts.fcr).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Legend verticalAlign="bottom" iconType="circle" height={40} wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Widget 3: Department Specific Analytics */}
        {id === "200" && data.charts?.sat_adjust && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-50">
              <CardTitle className="text-lg font-bold text-slate-800">Ajuste de Valor por SAT y Manejo del Agente</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.charts.sat_adjust} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#64748B' }} width={140} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#F59E0B" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {id === "201" && data.charts?.adherence && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-50">
              <CardTitle className="text-lg font-bold text-slate-800">Adherencia al Modelo de Servicio</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.charts.adherence}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {data.charts.adherence.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {id === "199" && data.charts?.stages && (
          <Card className="hover:shadow-md transition-shadow lg:col-span-2">
            <CardHeader className="pb-4 border-b border-slate-50">
              <CardTitle className="text-lg font-bold text-slate-800">Etapa del Proceso en la que se Encuentra el Caso</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.charts.stages}>
                    <defs>
                      <linearGradient id="colorStages" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorStages)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {id === "166" && data.charts?.personalize && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-50">
              <CardTitle className="text-lg font-bold text-slate-800">Validación de Bienvenida y Personalización</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.charts.personalize}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {id === "1" && data.charts?.opening_reason && (
          <Card className="hover:shadow-md transition-shadow lg:col-span-2">
            <CardHeader className="pb-4 border-b border-slate-50">
              <CardTitle className="text-lg font-bold text-slate-800">Identificación del Motivo de Apertura (CX)</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.charts.opening_reason}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#EC4899" radius={[4, 4, 0, 0]}>
                      {data.charts.opening_reason.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Department Charts */}
        {data.charts?.documentary_status && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-50">
              <CardTitle className="text-lg font-bold text-slate-800">Completitud del Expediente Aduanero</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.charts.documentary_status}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={85}
                      dataKey="value"
                    >
                      {data.charts.documentary_status.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index === 0 ? "#10B981" : "#F43F5E"} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {id === "199" && data.charts?.documentary_issues && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-50">
              <CardTitle className="text-lg font-bold text-slate-800">Cumplimiento de Requisitos Documentales</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.charts.documentary_issues}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      labelLine={false}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {data.charts.documentary_issues.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {id === "199" && data.charts?.sla && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4 border-b border-slate-50">
              <CardTitle className="text-lg font-bold text-slate-800">Cumplimiento de SLA (Atrasos)</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.charts.sla} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={120} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

      </div>

      {/* Bottom Table: Resumen de Interacciones */}
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <CardTitle className="text-lg font-bold text-slate-800">Detalle de Gestión y Resumen de Interacciones</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Registros procesados recientes y retroalimentación de agentes.</p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar agente o palabra..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-1.5 w-full bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all"
              />
            </div>
            <Button variant="outline" size="sm" className="flex items-center gap-2 border-slate-200 text-slate-700">
              <SlidersHorizontal className="w-4 h-4" />
              <span>Filtrar</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Agente</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Resumen de la Interacción</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTable.length > 0 ? (
                  filteredTable.map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-sky-50 flex items-center justify-center text-sky-700 font-bold text-sm">
                            {row.agent?.charAt(0)}
                          </div>
                          <span className="font-semibold text-slate-900 text-sm">{row.agent}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">{row.date}</td>
                      <td className="px-6 py-4 text-sm text-slate-600 max-w-md truncate">{row.summary}</td>
                      <td className="px-6 py-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-sky-600 hover:text-sky-800 hover:bg-sky-50 font-semibold"
                          onClick={() => navigate("/calls")}
                        >
                          Ver Detalles
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-400 text-sm">
                      No se encontraron llamadas que coincidan con la búsqueda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardBuilderPage;
